import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { PageHero } from "@/components/site/PageShell";
import { canonical } from "@/components/site/site-config";

const TITLE = "Re-Roof Permits in Citrus County, FL | Destination Roofing";
const DESCRIPTION = "How re-roof permits work in Citrus County, including product approvals, inspections, the Notice of Commencement and contractor-handled steps.";
const PATH = "/citrus-county-roof-permits";

export const Route = createFileRoute("/citrus-county-roof-permits")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonical(PATH) },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(
        breadcrumbListJsonLd([
          
          { label: "Re-Roof Permits in Citrus County", href: PATH },
        ]),
      ),
    }],
  }),
  component: CitrusCountyRoofPermitsPage,
});

function CitrusCountyRoofPermitsPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Re-Roof Permits in Citrus County", href: PATH }]} />
      <PageHero
        eyebrow="Homeowner Guide"
        title="Re-Roof Permits in Citrus County, FL: How the Process Works"
        subtitle="Most roof replacements in Citrus County need a building permit and inspections."
      />
      <main className="mx-auto max-w-4xl px-4 py-12 md:py-16">
        <article className="prose prose-slate max-w-none">
          <p className="text-lg leading-relaxed">
            Most roof replacements in Citrus County need a building permit and inspections. As a licensed Florida roofing contractor (CCC1335406), Destination Roofing pulls the permit and schedules the inspections for you.
          </p>

          <h2>Who issues the permit</h2>
          <p>
            Homes in unincorporated Citrus County go through the Citrus County Building Division at <a href="tel:+13525275310">352-527-5310</a>. The City of Inverness runs its own building department, and other city limits may too, so we confirm the right office for every job.
          </p>

          <h2>What the county requires</h2>
          <p>
            Citrus County&apos;s Re-Roofing Affidavit requires Florida product approval installation documents for the roofing product and the underlayment, an in-progress inspection and a final inspection.
          </p>

          <h2>Notice of Commencement</h2>
          <p>
            <a href="https://www.flsenate.gov/Laws/Statutes/2025/0713.135" target="_blank" rel="noopener noreferrer">Florida Statute 713.135</a> requires a recorded Notice of Commencement for most jobs where the contract is over $5,000. We explain what you need to sign.
          </p>

          <h2>How we handle it for you</h2>
          <ol>
            <li>Inspection and proposal.</li>
            <li>You sign the contract and Notice of Commencement.</li>
            <li>We submit the permit with product approvals.</li>
            <li>Tear-off and dry-in.</li>
            <li>In-progress inspection.</li>
            <li>Installation.</li>
            <li>Final inspection and cleanup.</li>
          </ol>

          <h2>Why a permitted roof matters</h2>
          <p>Permits create a record that inspectors approved the work, which insurers and future buyers may ask for.</p>

          <h2>Frequently asked questions</h2>
          <h3>Do most roof replacements in Citrus County need a permit?</h3>
          <p>Yes. Most roof replacements in Citrus County need a building permit and inspections.</p>
          <h3>Who handles the permit and inspections?</h3>
          <p>Destination Roofing pulls the permit and schedules the inspections for you.</p>
          <h3>What inspections does the county require?</h3>
          <p>Citrus County&apos;s Re-Roofing Affidavit requires an in-progress inspection and a final inspection.</p>

          <p className="text-sm text-muted-foreground"><strong>Updated September 2026</strong></p>
        </article>
      </main>
      <CTASection
        headline="Schedule My Free Roof Inspection"
        sub="Destination Roofing pulls the permit and schedules the inspections for your roof replacement."
      />
    </>
  );
}